import { useEffect, useRef, useState, type DragEvent } from 'react';
import { TOKEN_CAP, useGameStore } from '@/state/gameStore';
import { selectCowardStatus, selectTokens, selectUltimateCoward } from '@/state/selectors';
import styles from './TokenTray.module.css';

export const TOKEN_EJECT_MS = 620;

interface SlotProps {
  index: number;
  filled: boolean;
  ejecting: boolean;
  scarred: boolean;
  ultimate: boolean;
  draggableTokens: boolean;
}

function TokenSlot({ index, filled, ejecting, scarred, ultimate, draggableTokens }: SlotProps) {
  const showCoin = filled || ejecting;
  const label = String(index);
  const draggable = filled && draggableTokens;

  const handleDragStart = (event: DragEvent<HTMLSpanElement>) => {
    if (!draggable) return;
    event.dataTransfer.setData('text', 'TOKEN');
    event.dataTransfer.effectAllowed = 'move';
  };

  return (
    <span
      className={styles.slot}
      data-testid={`token-slot-${label}`}
      data-slot={label}
      data-filled={filled ? 'true' : 'false'}
      draggable={draggable}
      onDragStart={handleDragStart}
      {...(ejecting ? { 'data-ejecting': 'true' } : {})}
      {...(scarred ? { 'data-scarred': 'true' } : {})}
      {...(ultimate ? { 'data-ultimate': 'true' } : {})}
      aria-label={`Token ${String(index + 1)}: ${filled ? 'ready' : 'spent'}`}
    >
      {showCoin ? <span className={styles.coin} /> : null}
      <span className={styles.slotNumber}>{String(index + 1).padStart(2, '0')}</span>
      <span className={styles.slotState}>{filled ? 'READY' : 'SPENT'}</span>
    </span>
  );
}

export interface TokenTrayProps {
  draggableTokens?: boolean;
}

export function TokenTray({ draggableTokens = false }: TokenTrayProps) {
  const tokens = useGameStore(selectTokens);
  const coward = useGameStore(selectCowardStatus);
  const ultimate = useGameStore(selectUltimateCoward);

  const lastObservedTokens = useRef(tokens);
  const [ejectingSlot, setEjectingSlot] = useState<number | null>(null);

  useEffect(() => {
    const prev = lastObservedTokens.current;
    if (tokens >= prev) {
      lastObservedTokens.current = tokens;
      return undefined;
    }
    const slot = prev - 1;
    setEjectingSlot(slot);
    const timerId = window.setTimeout(() => {
      setEjectingSlot((current) => (current === slot ? null : current));
      lastObservedTokens.current = tokens;
    }, TOKEN_EJECT_MS);
    return () => {
      window.clearTimeout(timerId);
    };
  }, [tokens]);

  const slotCount = Math.max(TOKEN_CAP, tokens);
  const bonusTokens = Math.max(0, tokens - TOKEN_CAP);
  const countLabel =
    bonusTokens > 0
      ? `${String(TOKEN_CAP).padStart(2, '0')} + ${String(bonusTokens).padStart(2, '0')}`
      : `${String(tokens).padStart(2, '0')} / ${String(TOKEN_CAP).padStart(2, '0')}`;
  const damageLevel = ultimate ? 2 : coward ? 1 : 0;

  return (
    <div
      className={styles.tray}
      data-testid="token-tray"
      data-tokens={String(tokens)}
      data-draggable={draggableTokens ? 'true' : 'false'}
      role="status"
      aria-live="polite"
      aria-label={`${String(tokens)} assessment tokens remaining`}
    >
      <div className={styles.readout}>
        <span className={styles.title}>ASSESSMENT TOKENS</span>
        {damageLevel > 0 ? (
          <span className={styles.damage}>CHECKPOINT SCAR × {String(damageLevel)}</span>
        ) : null}
        <output className={styles.count} aria-label="Token count">
          {countLabel}
        </output>
      </div>
      <div className={styles.sockets}>
        {Array.from({ length: slotCount }, (_, i) => (
          <TokenSlot
            key={i}
            index={i}
            filled={i < tokens}
            ejecting={i === ejectingSlot}
            scarred={coward}
            ultimate={ultimate}
            draggableTokens={draggableTokens}
          />
        ))}
      </div>
    </div>
  );
}
